/**
 * Local landing page data for "Airbnb / short let cleaning and changeovers in
 * [city]" SEO pages. One entry per city, rendered by LocalCleaningPage.
 *
 * HONESTY NOTE (pre-launch): Ariv is building its local cleaner and trade
 * network city by city and does not yet have live supply in every area. The
 * copy here deliberately uses "we are building our network / join early"
 * framing, never "we already have cleaners in X". Each city carries genuinely
 * unique local context (areas, seasonality, local short-let rules) so these are
 * real local pages, not thin doorway duplicates that Google would penalise.
 */

export const CITIES = [
  {
    slug: 'edinburgh',
    name: 'Edinburgh',
    region: 'Scotland',
    intro:
      'Edinburgh is one of the busiest short stay markets in the UK, and turnovers spike hard around the August festivals, when a flawless same day changeover can be the difference between a five star review and a cancelled booking.',
    areas: ['Old Town', 'New Town', 'Leith', 'Stockbridge', 'Bruntsfield', 'Haymarket'],
    localNote: {
      title: 'Edinburgh has strict short let rules',
      body:
        'Edinburgh operates a short term let licensing scheme and is a designated control area, so hosts here manage compliance on top of cleaning and maintenance. Ariv is built with UK short let rules in mind, so the support fits how the city actually works.',
    },
    peak: 'August (Fringe and the International Festival) and Hogmanay at New Year.',
  },
  {
    slug: 'manchester',
    name: 'Manchester',
    region: 'North West England',
    intro:
      "Manchester's city centre is packed with short stay apartments serving football crowds, arena gigs, and a steady business travel base, which means frequent fast changeovers all year rather than a single summer peak.",
    areas: ['Northern Quarter', 'Ancoats', 'Deansgate', 'Salford Quays', 'Castlefield', 'Didsbury'],
    localNote: {
      title: 'Dense apartment blocks, quick turnarounds',
      body:
        'From Ancoats and the Northern Quarter to Salford Quays, Manchester demand sits in busy apartment buildings where reliable key access and a fast, consistent turnaround matter more than anything else.',
    },
    peak: 'Match days, arena and convention events, and year round business travel.',
  },
  {
    slug: 'brighton',
    name: 'Brighton',
    region: 'South East England',
    intro:
      'Brighton lives on weekend and event demand, from seafront conferences to summer crowds, so most hosts here face a Friday to Monday rush where a missed changeover is an expensive empty night.',
    areas: ['Kemptown', 'The Lanes', 'Hove', 'Seven Dials', 'Brunswick', 'Preston Park'],
    localNote: {
      title: 'Period seaside properties with their own quirks',
      body:
        'With stock spread across Kemptown, Hove, and the Lanes, and a lot of older conversions, Brighton hosts value cleaners and trades who understand the quirks of period seaside buildings.',
    },
    peak: 'Summer weekends, Brighton Pride, and the autumn conference season.',
  },
  {
    slug: 'bath',
    name: 'Bath',
    region: 'South West England',
    intro:
      'Bath draws heritage tourism through almost every month of the year, so its short let market is steadier than most, with Georgian terraces and listed buildings that need careful, consistent upkeep between guests.',
    areas: ['City Centre', 'Bathwick', 'Widcombe', 'Oldfield Park', 'Lansdown', 'Bear Flat'],
    localNote: {
      title: 'Listed buildings need sympathetic care',
      body:
        'Many Bath properties are listed or sit in conservation areas, so maintenance has to be done sympathetically. Hosts here want trades who understand that, not a generic call out.',
    },
    peak: 'Spring and autumn city breaks, plus the Christmas market season.',
  },
  {
    slug: 'cornwall',
    name: 'Cornwall',
    region: 'South West England',
    intro:
      'Cornwall is the heart of UK holiday letting, and its calendar is fiercely seasonal: a frantic summer of back to back weekly changeovers followed by a much quieter winter, which makes dependable peak season cleaning support the whole game.',
    areas: ['Newquay', 'St Ives', 'Padstow', 'Falmouth', 'Bude', 'Truro'],
    localNote: {
      title: 'Coverage at distance during the summer crush',
      body:
        'With properties scattered from St Ives to Padstow and plenty of remote coastal cottages, the Cornwall challenge is coverage and reliability at distance through the summer peak, not the quiet months.',
    },
    peak: 'The school summer holidays and bank holiday weekends.',
  },
];

export const getCity = (slug) => CITIES.find((c) => c.slug === slug);
